import React, { useEffect, useRef } from "react";
import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

/**
 * Composant code-barres EAN13 (rendu SVG via JsBarcode).
 * Extrait de Labels.jsx — iter22 refactor.
 */
export function Barcode({ value, height = 50, width = 1.8, displayValue = true }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current && value) {
      try {
        JsBarcode(ref.current, String(value), {
          format: "EAN13",
          height,
          width,
          displayValue,
          fontSize: 11,
          margin: 0,
          background: "#ffffff",
          lineColor: "#000000",
        });
      } catch {}
    }
  }, [value, height, width, displayValue]);
  return <svg ref={ref} />;
}

// Helpers de conversion canvas/svg → dataURL pour l'embarquement dans le PDF
export async function canvasToDataUrl(canvas) {
  return canvas.toDataURL("image/png");
}

export async function svgToDataUrl(svg) {
  const s = new XMLSerializer().serializeToString(svg);
  const blob = new Blob([s], { type: "image/svg+xml;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const img = new Image();
  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = rej;
    img.src = url;
  });
  const canvas = document.createElement("canvas");
  canvas.width = img.width || 300;
  canvas.height = img.height || 80;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0);
  URL.revokeObjectURL(url);
  return canvas.toDataURL("image/png");
}

/**
 * Phase 101gc.1 (Feb 2026) — Génère un code-barre EAN13 en PNG dataURL
 * SANS passer par le DOM (utilisé pour l'impression PDF batch d'un OF entier).
 *
 * Avant ce helper, seul le produit sélectionné dans l'écran Labels avait son
 * barcode rendu (via <Barcode /> dans le DOM). Résultat : les étiquettes des
 * AUTRES produits de l'OF n'avaient pas de code-barre dans le PDF.
 *
 * @param {string} value - EAN13 normalisé (13 chiffres)
 * @param {object} opts  - { height, width, displayValue, fontSize }
 * @returns {Promise<string|null>} - dataURL PNG ou null si valeur invalide
 */
export async function generateBarcodeDataUrl(value, opts = {}) {
  const v = String(value || "").replace(/\D/g, "");
  if (v.length !== 13) return null; // EAN13 doit faire 13 chiffres
  try {
    // Crée un <svg> off-DOM (pas attaché au document)
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    JsBarcode(svg, v, {
      format: "EAN13",
      height: opts.height ?? 50,
      width: opts.width ?? 1.8,
      displayValue: opts.displayValue ?? true,
      fontSize: opts.fontSize ?? 11,
      margin: 0,
      background: "#ffffff",
      lineColor: "#000000",
      xmlDocument: document, // requis pour que JsBarcode puisse manipuler le SVG hors DOM
    });
    return await svgToDataUrl(svg);
  } catch (e) {
    // Silent fallback : PDF reste valide sans barcode plutôt que planter
    console.warn("[Barcode] generateBarcodeDataUrl échec pour", value, e);
    return null;
  }
}

/**
 * Phase 101gc.1 — Génère un QR code en PNG dataURL sans passer par le DOM.
 * Utilisé pour l'impression PDF batch d'un OF entier.
 */
export async function generateQrDataUrl(value, opts = {}) {
  if (!value) return null;
  try {
    return await QRCode.toDataURL(String(value), {
      errorCorrectionLevel: opts.errorCorrectionLevel || "M",
      margin: opts.margin ?? 1,
      width: opts.width ?? 128,
      color: {
        dark: "#000000",
        light: "#FFFFFF",
      },
    });
  } catch (e) {
    console.warn("[Barcode] generateQrDataUrl échec pour", value, e);
    return null;
  }
}

export default Barcode;
